import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, ChevronRight, Mail, Phone, MapPin, ShieldCheck, Save } from "lucide-react";
import { api, fmt, ORDER_STYLES, mediaUrl } from "../lib/api";
import { useAuth } from "../context/Store";

export default function Account() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [address, setAddress] = useState(() => ({ name: user?.address?.name || user?.name || "", phone: user?.address?.phone || user?.phone || "", line: user?.address?.line || "", city: user?.address?.city || "", state: user?.address?.state || "", pincode: user?.address?.pincode || "" }));
  const [addressBusy, setAddressBusy] = useState(false);
  const [addressMessage, setAddressMessage] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");

  const fieldClass = "w-full rounded-xl border border-brand-ink/15 bg-white px-4 py-3 text-sm text-brand-ink outline-none focus:border-brand-blue";

  useEffect(() => {
    if (user === null) navigate("/auth?next=/account");
    if (user) {
      setAddress({ name: user.address?.name || user.name || "", phone: user.address?.phone || user.phone || "", line: user.address?.line || "", city: user.address?.city || "", state: user.address?.state || "", pincode: user.address?.pincode || "" });
      api.get("/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
    }
  }, [user, navigate]);

  if (!user) return null;

  const saveAddress = async (event) => {
    event.preventDefault();
    setAddressBusy(true); setAddressMessage("");
    try {
      const { data } = await api.put("/auth/account/address", address);
      setAddress((previous) => ({ ...previous, ...data.address }));
      setUser((previous) => ({ ...previous, phone: data.phone, address: data.address }));
      setAddressMessage("Your contact and delivery address have been saved.");
    } catch (error) { setAddressMessage(error.response?.data?.detail || "Could not save your details. Please try again."); }
    finally { setAddressBusy(false); }
  };

  const requestEmailCode = async () => {
    setEmailBusy(true); setEmailMessage("");
    try {
      const { data } = await api.post("/auth/email-verification/request");
      setEmailOtpSent(true); setEmailMessage(data.message || "Verification code sent.");
    } catch (error) { setEmailMessage(error.response?.data?.detail || "Could not send a verification code."); }
    finally { setEmailBusy(false); }
  };

  const confirmEmail = async (event) => {
    event.preventDefault(); setEmailBusy(true); setEmailMessage("");
    try {
      await api.post("/auth/email-verification/confirm", { otp: emailOtp });
      setUser((previous) => ({ ...previous, email_verified: true }));
      setEmailOtpSent(false); setEmailOtp(""); setEmailMessage("Your email is verified.");
    } catch (error) { setEmailMessage(error.response?.data?.detail || "That code could not be verified."); }
    finally { setEmailBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-28 md:pt-36 pb-24" data-testid="account-page">
      <p className="text-xs uppercase tracking-[0.25em] text-brand-blue font-semibold">My account</p>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">Hello, {user.name}</h1>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-brand-ink/60">
        <span>{user.email}</span>
        {user.is_partner && (
          <Link to="/partner" className="rounded-full brand-gradient-bg text-white px-4 py-1.5 text-xs font-bold" data-testid="account-partner-badge">
            Partner · {user.partner_id}
          </Link>
        )}
        {user.loyalty_balance > 0 && (
          <span className="rounded-full bg-brand-cyan/15 text-brand-blue px-4 py-1.5 text-xs font-bold" data-testid="loyalty-balance">
            Loyalty rewards: {fmt(user.loyalty_balance)}
          </span>
        )}
      </div>

      <section className="mt-10 grid gap-6 lg:grid-cols-2" aria-label="Account details">
        <div className="rounded-3xl border border-brand-ink/10 p-6 md:p-8">
          <h2 className="font-display text-2xl font-bold">Contact details</h2>
          <div className="mt-6 space-y-5">
            <div className="flex items-start gap-3">
              <Mail className="mt-1 h-5 w-5 text-brand-blue" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-ink/45">Email address</p>
                <p className="mt-1 break-all text-sm">{user.email}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${user.email_verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                    {user.email_verified ? "Verified" : "Not verified"}
                  </span>
                  {!user.email_verified && !user.member_view && <button type="button" disabled={emailBusy} onClick={requestEmailCode} className="text-xs font-semibold text-brand-blue hover:underline">{emailBusy ? "Sending…" : "Verify email"}</button>}
                </div>
                {emailOtpSent && <form onSubmit={confirmEmail} className="mt-3 flex flex-wrap gap-2">
                  <input aria-label="Email verification code" required inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className={`${fieldClass} max-w-44`} />
                  <button disabled={emailBusy || emailOtp.length !== 6} className="rounded-xl brand-gradient-bg px-4 py-3 text-sm font-semibold text-white">{emailBusy ? "Checking…" : "Confirm"}</button>
                </form>}
                {emailMessage && <p role="status" className="mt-2 text-xs text-brand-ink/60">{emailMessage}</p>}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="mt-1 h-5 w-5 text-brand-blue" />
              <div><p className="text-xs font-semibold uppercase tracking-wide text-brand-ink/45">Phone number</p><p className="mt-1 text-sm">{user.phone || "Not added"}</p></div>
            </div>
          </div>
        </div>

        <form onSubmit={saveAddress} className="rounded-3xl border border-brand-ink/10 p-6 md:p-8">
          <div className="flex items-center gap-2"><MapPin className="h-5 w-5 text-brand-blue" /><h2 className="font-display text-2xl font-bold">Delivery address</h2></div>
          <p className="mt-2 text-sm text-brand-ink/55">Add the details needed to deliver your orders.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-brand-ink/65">Recipient name<input required minLength={2} maxLength={80} value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} className={`${fieldClass} mt-1 font-normal`} autoComplete="name" /></label>
            <label className="text-xs font-semibold text-brand-ink/65">Phone number<input required type="tel" minLength={10} maxLength={24} value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className={`${fieldClass} mt-1 font-normal`} autoComplete="tel" /></label>
            <label className="text-xs font-semibold text-brand-ink/65 sm:col-span-2">Street address / flat, building<input required minLength={4} maxLength={200} value={address.line} onChange={(e) => setAddress({ ...address, line: e.target.value })} className={`${fieldClass} mt-1 font-normal`} autoComplete="street-address" /></label>
            <label className="text-xs font-semibold text-brand-ink/65">City<input required minLength={2} maxLength={60} value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className={`${fieldClass} mt-1 font-normal`} autoComplete="address-level2" /></label>
            <label className="text-xs font-semibold text-brand-ink/65">State<input required minLength={2} maxLength={60} value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className={`${fieldClass} mt-1 font-normal`} autoComplete="address-level1" /></label>
            <label className="text-xs font-semibold text-brand-ink/65">PIN code<input required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} className={`${fieldClass} mt-1 font-normal`} autoComplete="postal-code" /></label>
          </div>
          {addressMessage && <p role="status" className="mt-3 text-xs text-brand-ink/60">{addressMessage}</p>}
          <button type="submit" disabled={addressBusy || user.member_view} className="mt-5 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-3 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" />{addressBusy ? "Saving…" : "Save account details"}</button>
          {user.member_view && <p className="mt-2 text-xs text-amber-700"><ShieldCheck className="mr-1 inline h-3 w-3" />Account is read-only in admin view.</p>}
        </form>
      </section>

      <h2 className="font-display text-2xl font-bold mt-14 mb-6">Orders</h2>
      {orders === null ? (
        <p className="text-brand-ink/40">Loading...</p>
      ) : orders.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-brand-ink/15 py-16 text-center" data-testid="orders-empty">
          <Package className="h-10 w-10 text-brand-ink/20 mx-auto" />
          <p className="mt-4 text-brand-ink/50">No orders yet</p>
          <Link to="/shop" className="inline-block mt-4 text-sm font-semibold text-brand-blue">Start shopping →</Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.order_id} className="rounded-3xl border border-brand-ink/8 p-6 hover:border-brand-blue/30 transition-colors" data-testid={`order-${o.order_id}`}>
              <div className="flex flex-wrap items-center gap-4 justify-between">
                <div>
                  <p className="font-display font-bold">{o.order_id}</p>
                  <p className="text-xs text-brand-ink/40 mt-1">{new Date(o.created_at).toLocaleString("en-IN")}</p>
                </div>
                <span className={`rounded-full px-4 py-1.5 text-xs font-bold ${ORDER_STYLES[o.status] || "bg-brand-ink/5"}`} data-testid={`order-status-${o.order_id}`}>
                  {o.status}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-3 overflow-x-auto">
                {o.items.map((i) => (
                  <img key={i.product_id} src={mediaUrl(i.image)} alt={i.name} title={`${i.name} × ${i.qty}`} className="h-14 w-14 rounded-xl object-cover shrink-0" />
                ))}
                <ChevronRight className="h-4 w-4 text-brand-ink/20 shrink-0" />
                <span className="font-display font-bold shrink-0">{fmt(o.total)}</span>
              </div>
              <Link to={`/track/${o.order_id}`} data-testid={`track-${o.order_id}`}
                className="mt-3 mr-5 inline-block text-xs font-semibold text-brand-blue hover:underline">
                Track order →
              </Link>
              {!["CREATED", "CREATE_FAILED", "PAYMENT_FAILED"].includes(o.status) && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${o.order_id}/invoice`}
                  data-testid={`invoice-${o.order_id}`}
                  className="mt-3 inline-block text-xs font-semibold text-brand-blue hover:underline">
                  Download invoice (PDF)
                </a>
              )}
              {o.status === "CREATED" && (
                <p className="mt-3 text-xs text-amber-600">Payment pending — complete it from checkout or contact support.</p>
              )}
              {o.status === "PAYMENT_FAILED" && (
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-rose-600">
                  <span>Payment was cancelled or not completed. Your order is unpaid.</span>
                  <Link to="/checkout" className="font-semibold text-brand-blue hover:underline">Try checkout again</Link>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
